import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Text,
  Group,
  ActionIcon,
  Flex,
  Button,
  Card,
  Popover,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconBulb, IconX,
  IconDownload,
  IconShare,
  IconCopy,
  IconFileDescription,
  IconChevronDown,
} from '@tabler/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDiscoveryStore } from '../../stores/useDiscoveryStore';
import { useDiscoverySessionUrl } from '../../hooks/useDiscoverySessionUrl';
import { useExploreIdeaIdUrlParam } from './hooks/useExploreIdeaIdUrlParam';
import {
  useSessionDiscoveryIdeas,
  useSessionOnePagers,
  useCreateOnePager,
  useDiscoveryMessages,
} from '../../hooks/useDiscoveryAgent';
import { CreateIdeaFromChatModal } from '../../pages/ai-agent/createIdea/CreateIdeaFromChatModal';
import { getPersistedMessageId, buildCreateIdeaHiddenMessage } from '../../utils/agent/idea';
import { useKnowledgeData } from '../../hooks/useKnowledgeData';
import { useFields } from '../../hooks/useFields';
import {
  useIdeaDetail,
  useImpactAggregationProperties,
  useThemeImpact,
} from '../../hooks/ideaPage/useIdeaQueries';
import { formatBigMonetaryValue } from '../ideaNiv/Funcs';
import ImpactSkeleton from '../Themes/ImpactSkeleton';
import AccountsPopover from '../../pages/dynamic-data/impact/AccountsPopover';
import { ExploreBagelSparkleIcon } from './ExploreBagelSparkleIcon';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';
import { shareDiscoverySession, sendDiscoveryMessageWithRecovery } from '../../api/discoveryAgent';
import { getCsvDownloadConfig } from '../../api/customizations';
import { downloadIdeaToCSV } from '../../utils/downloadIdeaToCSV';
import { htmlToStructuredText } from '../../utils/htmlToStructuredText';
import { ProductAreaHeaderIcon, StatusHeaderIcon } from '../../icons/x-symbol-svgrepo-com';
import {
  ATTRIBUTE_BORDER,
  ATTRIBUTE_TEXT,
  ExploreIdeaAttributePill,
  ExploreIdeaAttributeTooltip,
} from './components/ExploreIdeaListRow';
import { buildComponentHierarchyPath } from '../general/BagelAccordionMenu';
import { getAssignedEvidences, getSuggestedEvidences } from '../../api/idea';
import { useEvidenceListData } from '../../hooks/useEvidenceListData';
import { RelatedProductFeaturesSection } from './components/RelatedProductFeaturesSection';
import { DiscoveryEvidenceListSection } from './discovery-evidence';
import HtmlContent from '../HtmlContent';

function containsHtmlMarkup(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

function getImpactValue(themeImpact: Record<string, unknown> | null, property: any): number {
  if (!themeImpact || !property) return 0;
  const propertyName = property.propertyName;
  const impactData = themeImpact[propertyName];
  if (!impactData) return 0;
  if (property.groupBy && property.impact?.groupByPath) {
    const groupKey = property.impact.groupByPath;
    const group = (impactData as any)[groupKey];
    return group?.count ?? group?.total ?? 0;
  }
  if (typeof impactData === 'object' && impactData !== null && 'total' in impactData) {
    return (impactData as { total: number }).total ?? 0;
  }
  return typeof impactData === 'number' ? impactData : 0;
}

function formatImpactValue(property: any, value: number): string {
  const isCompanies = ['orgId', 'company._id'].includes(property?.fieldPath);
  if (isCompanies) return value.toString();
  return formatBigMonetaryValue(value, property?.impact?.prefix);
}

function formatTimestamp(date: Date | null): string {
  if (!date) return '—';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${days[date.getDay()]}, ${d}.${m}.${y}`;
}

function buildInsightMarkdown(
  title: string,
  timestampStr: string,
  evidenceCount: number,
  summary: string,
  sortedImpactProperties: any[],
  themeImpact: Record<string, unknown> | null
): string {
  const lines: string[] = [
    `# ${title}`,
    '',
    timestampStr,
    '',
    `${evidenceCount} evidence${evidenceCount !== 1 ? 's' : ''}`,
    '',
  ];
  if (summary.trim()) {
    lines.push('## Summary', '', summary.trim(), '');
  }
  lines.push(
    '## Business Impact',
    '| Metric | Value |',
    '|---|---|'
  );
  for (const property of sortedImpactProperties) {
    const value = getImpactValue(themeImpact, property);
    const formatted = formatImpactValue(property, value);
    const label = property.label || property.propertyName || '';
    lines.push(`| ${label} | ${formatted} |`);
  }
  return lines.join('\n');
}

type ExploreInsightsPanelProps = {
  onClosePanel?: () => void;
};

export const ExploreInsightsPanel: React.FC<ExploreInsightsPanelProps> = ({ onClosePanel }) => {
  const closeInsights = useDiscoveryStore((s) => s.closeInsights);
  const auth0 = useAuth0();
  const { getAccessTokenSilently } = auth0;
  const { sessionId } = useDiscoverySessionUrl();
  const urlIdeaId = useExploreIdeaIdUrlParam(sessionId ?? '');
  const [searchParams, setSearchParams] = useSearchParams();

  const { artifactIdeaIdByAlias, refetch: refetchSessionIdeas } = useSessionDiscoveryIdeas(sessionId ?? '');

  const activeEvidenceIds = useDiscoveryStore((s) => s.activeEvidenceIds);
  const activeMessageTimestamp = useDiscoveryStore((s) => s.activeMessageTimestamp);
  const activeEvidenceLinkText = useDiscoveryStore((s) => s.activeEvidenceLinkText);
  const activeInsightSummary = useDiscoveryStore((s) => s.activeInsightSummary);
  const storeActiveArtifactId = useDiscoveryStore((s) => s.activeArtifactId);
  const storeArtifactRecordId = useDiscoveryStore((s) => s.activeArtifactRecordId);
  const selectedArtifactFromUrl = searchParams.get('selectedArtifact')?.trim() ?? '';
  const urlArtifactIdParam = searchParams.get('artifactId')?.trim() ?? '';
  const isIdeaInsightsView = Boolean(urlIdeaId);

  const { data: ideaDetail, isLoading: ideaDetailLoading } = useIdeaDetail(
    isIdeaInsightsView ? urlIdeaId : undefined
  );
  const { data: assignedEvidencesData, isLoading: assignedEvidencesLoading } = useQuery({
    queryKey: ['explore-insights-idea-evidences-assigned', urlIdeaId],
    queryFn: () => getAssignedEvidences(urlIdeaId, [], [], auth0),
    enabled: isIdeaInsightsView,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const { data: suggestedEvidencesData, isLoading: suggestedEvidencesLoading } = useQuery({
    queryKey: ['explore-insights-idea-evidences-suggested', urlIdeaId],
    queryFn: () => getSuggestedEvidences(urlIdeaId, [], [], auth0),
    enabled: isIdeaInsightsView,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const ideaEvidencesLoading = assignedEvidencesLoading || suggestedEvidencesLoading;
  const ideaEvidenceIds = useMemo(() => {
    const assigned = Array.isArray(assignedEvidencesData?.evidences)
      ? assignedEvidencesData.evidences
      : [];
    const suggested = Array.isArray(suggestedEvidencesData?.evidences)
      ? suggestedEvidencesData.evidences
      : [];
    return [...assigned, ...suggested]
      .map((evidence: { _id?: string }) => String(evidence._id ?? ''))
      .filter(Boolean);
  }, [assignedEvidencesData, suggestedEvidencesData]);

  // Same evidenceIds passed to <DiscoveryEvidenceListSection> below, so this shares that
  // query's cache (via useEvidenceListData's queryKey) instead of issuing a second /bulk call.
  // Only the first page loads by default; AccountsPopover drives it to completion itself
  // (via hasMoreBulkEvidences/loadMoreBulkEvidences) so its account list matches the full
  // aggregation-backed totalCount rather than just whatever page has been scrolled into view.
  const {
    evidences: bulkEvidences,
    hasNextPage: hasMoreBulkEvidences,
    fetchNextPage: loadMoreBulkEvidences,
    isLoading: isLoadingFirstBulkPage,
    isFetchingNextPage: isFetchingNextBulkPage,
  } = useEvidenceListData(isIdeaInsightsView ? [] : activeEvidenceIds);
  // isFetchingNextPage alone misses the very first page — AccountsPopover would treat
  // that as "not loading" and show "No accounts found" before any evidence has arrived.
  const isLoadingMoreBulkEvidences = isLoadingFirstBulkPage || isFetchingNextBulkPage;

  /** Alias / URL keys for matching session ideas when API stores alias (fallback when no persisted `_id`). */
  const artifactKeyForSessionIdeas = useMemo(() => {
    const fromStore = storeActiveArtifactId?.trim();
    if (fromStore) return fromStore;
    if (selectedArtifactFromUrl) return selectedArtifactFromUrl;
    if (urlArtifactIdParam) return urlArtifactIdParam;
    return '';
  }, [storeActiveArtifactId, selectedArtifactFromUrl, urlArtifactIdParam]);

  const artifactIdForCreatePayload = useMemo(() => {
    const record = storeArtifactRecordId?.trim();
    if (record) return record;
    return artifactKeyForSessionIdeas.trim();
  }, [storeArtifactRecordId, artifactKeyForSessionIdeas]);

  const title = isIdeaInsightsView
    ? decodeHtmlEntities(String(ideaDetail?.title ?? 'Idea'))
    : decodeHtmlEntities(activeEvidenceLinkText || 'Insight');
  const evidenceCount = isIdeaInsightsView ? ideaEvidenceIds.length : activeEvidenceIds.length;
  const timestampStr = formatTimestamp(activeMessageTimestamp);
  const hasLinkedIdeaForSelectedArtifact = Boolean(
    (storeArtifactRecordId?.trim() && artifactIdeaIdByAlias.get(storeArtifactRecordId.trim())) ||
      (artifactKeyForSessionIdeas && artifactIdeaIdByAlias.get(artifactKeyForSessionIdeas))
  );
  const [sharePopoverOpen, setSharePopoverOpen] = useState(false);

  // ── One-pager (Explore insight artifacts only — ideas already have their own description) ──
  const [onePagerModalOpen, setOnePagerModalOpen] = useState(false);
  const effectiveArtifactId = !isIdeaInsightsView ? (artifactIdForCreatePayload || undefined) : undefined;
  const { data: sessionOnePagers = [] } = useSessionOnePagers(sessionId ?? '');
  const artifactOnePager = effectiveArtifactId
    ? sessionOnePagers.find((p) => p.artifactId === effectiveArtifactId) ?? null
    : null;
  const createOnePagerByArtifactMutation = useCreateOnePager(sessionId ?? '');

  // Find the agent message _id for the active artifact (messageId in one-pager schema = agent _id).
  const {
    messages: sessionMessages = [],
    hasNextPage: hasMoreSessionMessages,
    fetchNextPage: fetchMoreSessionMessages,
  } = useDiscoveryMessages(sessionId ?? '');
  const findAgentMessageForArtifact = (messages: typeof sessionMessages) =>
    messages.find(
      (m) => m.messageType === 'agent' &&
        m.metadata?.artifacts?.some((a) => a._id === effectiveArtifactId || a.alias === effectiveArtifactId)
    );
  const artifactAgentMessageId = useMemo(() => {
    if (!effectiveArtifactId) return '';
    const agentMsg = findAgentMessageForArtifact(sessionMessages);
    return agentMsg ? (getPersistedMessageId(agentMsg) ?? '') : '';
  }, [effectiveArtifactId, sessionMessages]);

  const insightsPanelFetchContent = useCallback(async (): Promise<{
    title: string;
    description: string;
    alreadyPersisted?: boolean;
  }> => {
    if (!effectiveArtifactId || !sessionId) {
      return { title: '', description: '' };
    }
    if (artifactOnePager) {
      return { title: artifactOnePager.title, description: artifactOnePager.description, alreadyPersisted: true };
    }
    let agentMessageId = artifactAgentMessageId;
    if (!agentMessageId) {
      if (findAgentMessageForArtifact(sessionMessages)) {
        throw new Error("This insight's message hasn't finished saving yet — try again in a moment.");
      }
      let pageResult = { hasNextPage: hasMoreSessionMessages, found: undefined as ReturnType<typeof findAgentMessageForArtifact> };
      while (!pageResult.found && pageResult.hasNextPage) {
        const fetched = await fetchMoreSessionMessages();
        const messages = (fetched.data?.pages ?? [])
          .flatMap((p) => p.messages ?? [])
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        pageResult = { hasNextPage: fetched.hasNextPage ?? false, found: findAgentMessageForArtifact(messages) };
      }
      if (!pageResult.found) {
        throw new Error('Could not find the chat message for this insight.');
      }
      agentMessageId = getPersistedMessageId(pageResult.found) ?? '';
      if (!agentMessageId) {
        throw new Error("This insight's message hasn't finished saving yet — try again in a moment.");
      }
    }
    // Call /chat to generate title + description
    const hiddenMsg = buildCreateIdeaHiddenMessage(title);
    const result = await sendDiscoveryMessageWithRecovery(
      sessionId,
      hiddenMsg,
      auth0,
      undefined,
      { hidden: true, recoveryBudgetMs: 90_000 }
    );
    if (!result.success || result.status === 'pending') {
      throw new Error(result.message || 'Failed to generate one-pager content');
    }
    const chatArtifact = result.artifacts?.[0];
    const generatedTitle = chatArtifact?.title ?? '';
    const generatedDescription = chatArtifact?.description ?? '';
    // Save to /one-pager with artifactId — mutation updates session cache
    await createOnePagerByArtifactMutation.mutateAsync({
      sessionId,
      messageId: agentMessageId,
      artifactId: effectiveArtifactId,
      queryText: title,
      title: generatedTitle,
      description: generatedDescription,
    });
    return { title: generatedTitle, description: generatedDescription, alreadyPersisted: true };
  }, [artifactOnePager, effectiveArtifactId, sessionId, artifactAgentMessageId, hasMoreSessionMessages, sessionMessages, fetchMoreSessionMessages, auth0, createOnePagerByArtifactMutation, title]);

  const { defaultStatus, defaultPriority } = useKnowledgeData();
  const { ideaStatuses, ideaPriorities, allComponents } = useFields();

  const ideaStatusMeta = useMemo(() => {
    const statusKey = String(ideaDetail?.status ?? '');
    const statusMeta = (ideaStatuses || []).find(
      (s: { key?: string }) => String(s.key ?? '') === statusKey
    );
    return {
      label: String((statusMeta as { label?: string })?.label ?? statusKey ?? '—') || '—',
      color: String((statusMeta as { color?: string })?.color ?? '#868E96'),
    };
  }, [ideaDetail?.status, ideaStatuses]);
  const ideaComponentObj = useMemo(() => {
    const componentObj = ideaDetail?.componentObj as Record<string, unknown> | undefined;
    if (componentObj && typeof componentObj === 'object') return componentObj;
    const component = ideaDetail?.component as Record<string, unknown> | undefined;
    return component && typeof component === 'object' ? component : undefined;
  }, [ideaDetail]);
  const ideaComponentName = useMemo(() => {
    const componentObj = ideaDetail?.componentObj as { name?: string } | undefined;
    if (componentObj?.name?.trim()) return componentObj.name.trim();
    const component = ideaDetail?.component as { name?: string } | undefined;
    return component?.name?.trim() ?? '';
  }, [ideaDetail]);
  const ideaOwnerName = useMemo(() => {
    const ownerObj = ideaDetail?.ownerObj as { name?: string } | undefined;
    if (ownerObj?.name?.trim()) return ownerObj.name.trim();
    const owner = ideaDetail?.owner as { name?: string } | undefined;
    return owner?.name?.trim() ?? '';
  }, [ideaDetail]);
  const ideaOwnerInitials = useMemo(() => {
    const parts = ideaOwnerName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toLowerCase();
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toLowerCase();
  }, [ideaOwnerName]);

  const statusSelectData = useMemo(
    () =>
      (ideaStatuses || []).map((s: { key?: string; label?: string }) => ({
        value: String(s.key ?? ''),
        label: String(s.label ?? s.key ?? ''),
      })),
    [ideaStatuses]
  );

  const prioritySelectData = useMemo(
    () =>
      (ideaPriorities || []).map((p: { key?: string; label?: string }) => ({
        value: String(p.key ?? ''),
        label: String(p.label ?? p.key ?? ''),
      })),
    [ideaPriorities]
  );

  const resolveDefaultStatusKey = useCallback(() => {
    const fromList = ideaStatuses?.find((s: { isDefault?: boolean }) => s.isDefault)?.key;
    const fallback = defaultStatus ?? statusSelectData[0]?.value ?? '';
    return String(fromList ?? fallback ?? '');
  }, [ideaStatuses, defaultStatus, statusSelectData]);

  const resolveDefaultPriorityKey = useCallback(() => {
    const fromList = ideaPriorities?.find((p: { isDefault?: boolean }) => p.isDefault)?.key;
    const fallback = defaultPriority ?? prioritySelectData[0]?.value ?? '';
    return String(fromList ?? fallback ?? '');
  }, [ideaPriorities, defaultPriority, prioritySelectData]);

  const descriptionText = isIdeaInsightsView
    ? String(ideaDetail?.description ?? '').trim()
    : activeInsightSummary.trim();

  const descriptionForMarkdown = useMemo(() => {
    if (!descriptionText) return '';
    return isIdeaInsightsView ? htmlToStructuredText(descriptionText) : descriptionText;
  }, [descriptionText, isIdeaInsightsView]);

  const DESCRIPTION_CLAMP_LINE_COUNT = 5;
  const DESCRIPTION_CLAMP_MAX_HEIGHT = DESCRIPTION_CLAMP_LINE_COUNT * 16 * 1.5;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setDescriptionExpanded(false);
  }, [descriptionText]);

  useLayoutEffect(() => {
    if (!descriptionRef.current) {
      setDescriptionOverflows(false);
      return;
    }
    setDescriptionOverflows(descriptionRef.current.scrollHeight > DESCRIPTION_CLAMP_MAX_HEIGHT + 1);
  }, [descriptionText, DESCRIPTION_CLAMP_MAX_HEIGHT]);

  const { data: impactData, isLoading: impactLoading } = useThemeImpact(
    isIdeaInsightsView ? undefined : activeEvidenceIds
  );
  const { data: impactAggregationPropertiesData } = useImpactAggregationProperties();
  const rawProperties =
    impactAggregationPropertiesData?.impactAggregationProperties ??
    impactAggregationPropertiesData;
  const impactAggregationProperties = Array.isArray(rawProperties) ? rawProperties : [];

  const themeImpact = useMemo(() => {
    if (isIdeaInsightsView) {
      return (ideaDetail?.aggregations as Record<string, unknown> | undefined) ?? null;
    }
    if (impactData?.aggregations?.[0]) {
      return impactData.aggregations[0] as Record<string, unknown>;
    }
    return null;
  }, [impactData, ideaDetail?.aggregations, isIdeaInsightsView]);

  const resolvedImpactLoading = isIdeaInsightsView
    ? ideaDetailLoading
    : impactLoading;

  const sortedImpactProperties = useMemo(() => {
    if (!impactAggregationProperties || impactAggregationProperties.length === 0) return [];
    return [...impactAggregationProperties]
      .sort((a: any, b: any) => (a.impact?.index ?? 0) - (b.impact?.index ?? 0))
      .slice(0, 3);
  }, [impactAggregationProperties]);

  const handleInsightsPanelClose = useCallback(() => {
    // Remove deep-link params first to prevent re-opening via artifact effect.
    const next = new URLSearchParams(searchParams);
    next.delete('artifactId');
    next.delete('selectedArtifact');
    next.delete('ideaId');
    next.delete('ideasPage');
    setSearchParams(next, { replace: true });
    if (onClosePanel) {
      onClosePanel();
      return;
    }
    closeInsights();
  }, [searchParams, setSearchParams, onClosePanel, closeInsights]);

  const handleCopy = useCallback(async () => {
    const md = buildInsightMarkdown(
      title,
      timestampStr,
      evidenceCount,
      descriptionForMarkdown,
      sortedImpactProperties,
      themeImpact
    );
    await navigator.clipboard.writeText(md);
    showNotification({ message: 'Copied to clipboard', color: 'green' });
  }, [title, timestampStr, evidenceCount, descriptionForMarkdown, sortedImpactProperties, themeImpact]);

  const handleDownload = useCallback(() => {
    const md = buildInsightMarkdown(
      title,
      timestampStr,
      evidenceCount,
      descriptionForMarkdown,
      sortedImpactProperties,
      themeImpact
    );
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'insight'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [title, timestampStr, evidenceCount, descriptionForMarkdown, sortedImpactProperties, themeImpact]);

  const handleDownloadIdeaCsv = useCallback(async () => {
    const assigned = Array.isArray(assignedEvidencesData?.evidences)
      ? assignedEvidencesData.evidences
      : [];
    const suggested = Array.isArray(suggestedEvidencesData?.evidences)
      ? suggestedEvidencesData.evidences
      : [];
    let customConfig: { columns: any[] } | undefined;
    try {
      const result = await getCsvDownloadConfig(auth0);
      customConfig = result?.data;
    } catch (err) {
      customConfig = undefined;
    }
    downloadIdeaToCSV({
      idea: ideaDetail,
      allRequests: assigned,
      matches: suggested,
      requestsMatches: [],
      customConfig,
    });
  }, [ideaDetail, assignedEvidencesData, suggestedEvidencesData, auth0]);

  const handleCopyIdeaLink = useCallback(async () => {
    if (!urlIdeaId) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/idea/${urlIdeaId}`);
      showNotification({ message: 'Link copied to clipboard', color: 'green' });
    } catch (err) {
      showNotification({
        message: err instanceof Error ? err.message : 'Failed to copy link',
        color: 'red',
      });
    }
  }, [urlIdeaId]);

  const handleShareConfirm = useCallback(async () => {
    if (!sessionId) return;
    try {
      await shareDiscoverySession(sessionId, { getAccessTokenSilently });
      await navigator.clipboard.writeText(window.location.href);
      showNotification({ message: 'Link copied to clipboard', color: 'green' });
      setSharePopoverOpen(false);
    } catch (err) {
      showNotification({
        message: err instanceof Error ? err.message : 'Failed to share session',
        color: 'red',
      });
    }
  }, [sessionId, getAccessTokenSilently]);

  if (!isIdeaInsightsView && activeEvidenceIds.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #E9ECEF',
          }}
        >
          <ActionIcon
            size="sm"
            variant="subtle"
            color="dark"
            onClick={handleInsightsPanelClose}
            aria-label="Close insights panel"
          >
            <IconX size={18} />
          </ActionIcon>
        </Box>
        <Box p="md">
          <Text size="sm" c="dimmed">
            Click an evidence link in the chat to see aggregated impact here.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '22px 20px'
          }}
        >
          <ActionIcon
            size="md"
            variant="subtle"
            onClick={handleInsightsPanelClose}
            aria-label="Close insights panel"
          >
            <IconX size={18} color="#212529"/>
          </ActionIcon>
          <Group spacing="xs">
            {!isIdeaInsightsView && !hasLinkedIdeaForSelectedArtifact && effectiveArtifactId && (
              <Tooltip
                label={artifactOnePager ? 'View one-pager for this insight' : 'Create an Idea from this topic'}
                position="bottom"
                withArrow
                openDelay={400}
              >
                <Flex
                  data-testid="explore-insights-create-idea"
                  align="center"
                  gap={5}
                  px={10}
                  py={5}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!effectiveArtifactId) return;
                    setOnePagerModalOpen(true);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!effectiveArtifactId) return;
                      setOnePagerModalOpen(true);
                    }
                  }}
                  sx={{
                    borderRadius: 20,
                    border: '1px solid #5C5CEB55',
                    backgroundColor: '#5C5CEB0D',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#5C5CEB1A',
                      borderColor: '#5C5CEB99',
                    },
                  }}
                >
                  {artifactOnePager
                    ? <IconFileDescription size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
                    : <IconBulb size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
                  }
                  <Text size="xs" fw={500} color="#5C5CEB" sx={{ whiteSpace: 'nowrap' }}>
                    {artifactOnePager ? 'Show One-Pager' : 'Create One-Pager'}
                  </Text>
                </Flex>
              </Tooltip>
            )}
            <ActionIcon
              size="md"
              variant="subtle"
              aria-label="Copy markdown"
              onClick={handleCopy}
            >
              <IconCopy size={18} color="#7B808D" />
            </ActionIcon>
            <ActionIcon
              size="md"
              variant="subtle"
              aria-label={isIdeaInsightsView ? 'Download idea CSV' : 'Download markdown'}
              onClick={isIdeaInsightsView ? handleDownloadIdeaCsv : handleDownload}
            >
              <IconDownload size={18} color="#7B808D" />
            </ActionIcon>
            <Popover
              opened={!isIdeaInsightsView && sharePopoverOpen}
              onClose={() => setSharePopoverOpen(false)}
              position="bottom-end"
              withArrow
              shadow="md"
            >
              <Popover.Target>
                <Button
                  variant="outline"
                  rightIcon={<IconShare size={18} color="#212529" />}
                  size="sm"
                  onClick={() => {
                    if (isIdeaInsightsView) {
                      handleCopyIdeaLink();
                    } else {
                      setSharePopoverOpen((o) => !o);
                    }
                  }}
                  sx={{
                    height: 32,
                    borderColor: '#D8D8DB',
                    color: '#212529',
                    fontSize: 12,
                    fontWeight: 500,
                    ':hover': { backgroundColor: '#5C5CEB1A' },
                  }}
                >
                  Share
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="sm" mb="xs">
                  Make this session shareable and copy link?
                </Text>
                <Button size="xs" onClick={handleShareConfirm} disabled={!sessionId}>
                  Copy link
                </Button>
              </Popover.Dropdown>
            </Popover>
          </Group>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            maxWidth: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '20px 40px 40px',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {isIdeaInsightsView && (
              <Flex align="center" gap={8} wrap="wrap" sx={{ minHeight: 24 }}>
                <Flex
                  align="center"
                  gap={4}
                  sx={{
                    height: 24,
                    paddingInline: 8,
                    borderRadius: 6,
                    backgroundColor: '#F1EFFD',
                    color: '#585C68',
                    flexShrink: 0,
                  }}
                >
                  <IconBulb size={14} />
                  <Text size="xs" fw={700} c="#585C68" sx={{ fontSize: 12, letterSpacing: 0.3 }}>
                    IDEA
                  </Text>
                </Flex>
                <ExploreIdeaAttributePill
                  label={ideaStatusMeta.label}
                  icon={<StatusHeaderIcon />}
                  tooltipType="status"
                />
                <ExploreIdeaAttributePill
                  label={ideaComponentName || 'Unassigned'}
                  icon={<ProductAreaHeaderIcon />}
                  tooltipType="product area"
                  tooltipContent={buildComponentHierarchyPath(ideaComponentObj, allComponents)}
                  showEllipsisPrefix={Boolean(ideaComponentName)}
                />
                {ideaOwnerName ? (
                  <ExploreIdeaAttributeTooltip typeLabel="Owner" value={ideaOwnerName}>
                    <Flex align="center" gap={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
                      <Avatar
                        radius="xl"
                        size={22}
                        sx={{
                          border: `1px solid ${ATTRIBUTE_BORDER}`,
                          backgroundColor: '#F1F3F5',
                          color: ATTRIBUTE_TEXT,
                          fontSize: 10,
                          fontWeight: 600,
                          textTransform: 'lowercase',
                        }}
                      >
                        {ideaOwnerInitials || '?'}
                      </Avatar>
                      <Text size="xs" c={ATTRIBUTE_TEXT} lineClamp={1} sx={{ fontSize: 12 }}>
                        {ideaOwnerName}
                      </Text>
                    </Flex>
                  </ExploreIdeaAttributeTooltip>
                ) : null}
              </Flex>
            )}

            {/* Title and metadata */}
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Text fw={500} c="#2B2B2B" sx={{ fontSize: 32, marginBottom: 6, wordBreak: 'break-word' }}>
                {title}
              </Text>
              <Text c="#585C68" sx={{ fontSize: 14 }}>
                {formatTimestamp(activeMessageTimestamp)}
              </Text>
            </Box>

            {/* Description / summary */}
            <Box>
              {descriptionText ? (
                isIdeaInsightsView || containsHtmlMarkup(descriptionText) ? (
                  <>
                    <Box
                      ref={descriptionRef}
                      c="#2B2B2B"
                      sx={{
                        fontSize: 16,
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        maxHeight: descriptionExpanded ? 'none' : DESCRIPTION_CLAMP_MAX_HEIGHT,
                        '& h1, & h2, & h3, & h4': { marginTop: 16, marginBottom: 8, fontWeight: 600 },
                        '& p': { marginTop: 0, marginBottom: 12 },
                        '& ul, & ol': { marginTop: 0, marginBottom: 12, paddingLeft: 24 },
                        '& li': { marginBottom: 4 },
                        '& strong': { fontWeight: 600 },
                      }}
                    >
                      <HtmlContent content={descriptionText} />
                    </Box>
                    {(descriptionOverflows || descriptionExpanded) && (
                      <Text
                        c="#5C5CEB"
                        fw={500}
                        sx={{ fontSize: 14, cursor: 'pointer', userSelect: 'none', marginTop: 4 }}
                        onClick={() => setDescriptionExpanded((expanded) => !expanded)}
                      >
                        {descriptionExpanded ? 'Show less' : 'Show more'}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text c="#2B2B2B" sx={{ fontSize: 16 }}>
                    {descriptionText}
                  </Text>
                )
              ) : (
                <Text c="dimmed" sx={{ fontSize: 16 }}>
                  {isIdeaInsightsView
                    ? `No description for this idea yet. This view includes ${evidenceCount} evidence${
                      evidenceCount !== 1 ? 's' : ''
                    }.`
                    : `No summary for this insight yet. This view includes ${evidenceCount} evidence${
                      evidenceCount !== 1 ? 's' : ''
                    }.`}
                </Text>
              )}
            </Box>

            {/* Business Impact card */}
            <Card
              padding="md"
              radius="md"
              withBorder={false}
              sx={{
                backgroundColor: '#F8F9FA',
                borderRadius: 8,
                border: '1.3px solid #F3F3F3',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <Flex align="center" justify="space-between" mb="md">
                <Text fw={500} c="#212529" sx={{ fontSize: 16 }}>
                  Business Impact
                </Text>
                <ActionIcon
                  size="lg"
                  color="orange"
                  variant="subtle"
                  sx={{
                    padding: 4,
                    border: '1px solid #D8D8DB',
                    borderRadius: 6,
                    '&:hover': { backgroundColor: '#EBEBEC' },
                  }}
                >
                  <ExploreBagelSparkleIcon variant="insights" />
                </ActionIcon>
              </Flex>
              {sortedImpactProperties.length > 0 ? (
                resolvedImpactLoading ? (
                  <ImpactSkeleton count={sortedImpactProperties.length} />
                ) : (
                  <Box
                    sx={{
                      overflowX: 'auto',
                      maxWidth: '100%',
                      minWidth: 0,
                      paddingBottom: 4,
                    }}
                  >
                    <Group spacing="xl" sx={{ flexWrap: 'nowrap' }}>
                      {sortedImpactProperties.map((property: any, index: number) => {
                        const value = getImpactValue(themeImpact, property);
                        const formattedValue = formatImpactValue(property, value);
                        const label = property.label || property.propertyName || '';
                        const isCompanies = ['orgId', 'company._id'].includes(property?.fieldPath);
                        const showAccountsPopover = isCompanies;

                        const statContent = (
                          <>
                            <Text fw={700} sx={{ fontSize: 64 }} c="#2B2B2B">
                              {formattedValue}
                            </Text>
                            <Flex align="center" gap={4}>
                              <Text c="#585C68" sx={{ fontSize: 14 }}>
                                {label}
                              </Text>
                              {showAccountsPopover && (
                                <IconChevronDown size={14} color="#5C5CEB" style={{ opacity: 0.6 }} />
                              )}
                            </Flex>
                          </>
                        );

                        return showAccountsPopover ? (
                          <AccountsPopover
                            key={index}
                            totalCount={value}
                            assignedEvidences={isIdeaInsightsView ? assignedEvidencesData?.evidences : bulkEvidences}
                            suggestedEvidences={isIdeaInsightsView ? suggestedEvidencesData?.evidences : undefined}
                            hasMoreEvidences={!isIdeaInsightsView && hasMoreBulkEvidences}
                            isLoadingMoreEvidences={!isIdeaInsightsView && isLoadingMoreBulkEvidences}
                            loadMoreEvidences={isIdeaInsightsView ? undefined : loadMoreBulkEvidences}
                          >
                            <Box sx={{ flexShrink: 0 }}>{statContent}</Box>
                          </AccountsPopover>
                        ) : (
                          <Box key={index} sx={{ flexShrink: 0 }}>
                            {statContent}
                          </Box>
                        );
                      })}
                    </Group>
                  </Box>
                )
              ) : (
                <Text size="sm" c="dimmed">
                  No impact metrics are configured for this view yet.
                </Text>
              )}
            </Card>

            {!isIdeaInsightsView && evidenceCount > 1 && (
              <Card
                py="md"
                px="0"
                radius="md"
                withBorder={false}
                sx={{
                  backgroundColor: '#F8F9FA',
                  borderRadius: 8,
                  border: '1.3px solid #F3F3F3',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth: '100%',
                  width: '100%',
                }}
              >
                <RelatedProductFeaturesSection evidenceIds={activeEvidenceIds} />
              </Card>
            )}

            <Card
              py="md"
              px="0"
              radius="md"
              withBorder={false}
              sx={{
                backgroundColor: '#F8F9FA',
                borderRadius: 8,
                border: '1.3px solid #F3F3F3',
                overflow: 'hidden',
                minWidth: 0,
                maxWidth: '100%',
                width: '100%',
              }}
            >
              {isIdeaInsightsView && ideaEvidencesLoading ? (
                <Box px="md">
                  <ImpactSkeleton count={3} />
                </Box>
              ) : (
                <DiscoveryEvidenceListSection
                  evidenceIds={isIdeaInsightsView ? ideaEvidenceIds : activeEvidenceIds}
                  isIdeaArtifact={isIdeaInsightsView}
                />
              )}
            </Card>
          </Box>
        </Box>
      </Box>

      {Boolean(onePagerModalOpen && effectiveArtifactId) && (
        <CreateIdeaFromChatModal
          modalTestId="explore-insights-one-pager-modal"
          opened={onePagerModalOpen}
          onClose={() => setOnePagerModalOpen(false)}
          selectedOption={{
            label: title,
            payload: title,
            messageId: undefined,
          }}
          fetchContent={insightsPanelFetchContent}
          ideaId=""
          createdBy="agent"
          sessionId={sessionId ?? ''}
          messageId={undefined}
          artifactId={effectiveArtifactId}
          persistOnePager={false}
          onCreated={() => refetchSessionIdeas()}
        />
      )}
    </>
  );
};

export default ExploreInsightsPanel;
